import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, CheckCircle2, Filter } from "lucide-react";
import api from "@/lib/api";
import { scenarioTitle, scenarioSubtitle } from "@/lib/scenarioTitle";
import Disclaimer from "@/components/Disclaimer";
import { PacksSection } from "@/components/PacksSection";

const DIFF = { BEGINNER: "text-tc-bull bg-tc-bull/10", INTERMEDIATE: "text-tc-xp bg-tc-xp/10", ADVANCED: "text-tc-bear bg-tc-bear/10" };

export default function ScenarioLibrary() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/market/scenarios").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-tc-muted font-mono text-sm">Loading real markets…</div>;
  const list = cat ? data.scenarios.filter((s) => s.category === cat) : data.scenarios;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Scenario Library</h1>
        <p className="text-tc-sub mt-1">Real historical BTC/ETH markets, mined into classified training problems. The future is always hidden until you finish.</p>
      </div>
      <PacksSection />
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-tc-muted" />
        <button onClick={() => setCat(null)} className={`text-[11px] font-mono px-2.5 py-1 rounded-md border ${!cat ? "bg-tc-coach/15 border-tc-coach/40 text-tc-coach" : "border-white/10 text-tc-muted"}`}>ALL</button>
        {data.categories.map((c) => (
          <button key={c} data-testid={`filter-${c}`} onClick={() => setCat(c)}
            className={`text-[11px] font-mono px-2.5 py-1 rounded-md border ${cat === c ? "bg-tc-coach/15 border-tc-coach/40 text-tc-coach" : "border-white/10 text-tc-muted hover:text-tc-sub"}`}>{c}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <button key={s.id} data-testid={`scenario-${s.id}`} onClick={() => navigate(`/replay?scenario=${s.id}`)}
            className="tc-card p-5 text-left hover:border-tc-coach/40 hover:-translate-y-0.5 transition-all group">
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold text-lg">{s.symbol}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-coach/15 text-tc-coach">{s.interval} · REAL</span>
            </div>
            <div className="font-semibold mt-2 flex items-center gap-2" data-testid={`scenario-title-${s.id}`}>{scenarioTitle(s)} {s.completed && <CheckCircle2 className="w-4 h-4 text-tc-bull" />}</div>
            <div className="text-[11px] font-mono text-tc-muted mt-0.5">{scenarioSubtitle(s)}</div>
            <div className="text-sm text-tc-sub mt-1">{s.teaching_objective}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-tc-sub">{s.category}</span>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${DIFF[s.difficulty] || "bg-white/5 text-tc-sub"}`}>{s.difficulty}</span>
              {(s.skills || []).slice(0, 2).map((sk) => <span key={sk} className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-mastery/10 text-tc-mastery">{sk}</span>)}
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-tc-coach text-sm font-medium"><Lock className="w-3.5 h-3.5" /> Begin Analysis <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-tc-muted font-mono">{data.disclaimer}</p>
      <Disclaimer />
    </div>
  );
}
